import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import FluidBackground from "@/components/FluidBackground";
import { useReducedMotion } from "@/hooks/useReducedMotion";

const FLAVORS = [
  { name: "Juicy Red", color: "#FF3B2F", img: "https://images.pexels.com/photos/28617334/pexels-photo-28617334.jpeg" },
  { name: "Heavenly Yellow", color: "#FFC300", img: "https://images.pexels.com/photos/28902894/pexels-photo-28902894.jpeg" },
  { name: "Spicy Surprise", color: "#B5179E", img: "https://images.pexels.com/photos/1189261/pexels-photo-1189261.jpeg" },
];

export default function Hero({ settings, onOrder, onDiscover }) {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBack = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : 200]);
  const yFront = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : -80]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, reduced ? 1 : 0]);

  return (
    <section id="home" ref={ref} className="relative flex min-h-[100svh] items-center overflow-hidden bg-sfc-night pt-24">
      <motion.div style={{ y: yBack }} className="absolute inset-0">
        <FluidBackground variant="warm" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-sfc-night/30 via-transparent to-sfc-night" />

      <motion.div style={{ opacity }} className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2 lg:px-10">
        <div>
          <motion.span
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-block rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.25em] text-sfc-yellow"
          >
            {settings?.hero_subtitle || "Exotische drankjes"}
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="mt-6 font-display text-5xl font-semibold leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl"
          >
            {settings?.hero_title || "Proef het buitengewone."}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-6 max-w-lg text-base leading-relaxed text-white/70 sm:text-lg"
          >
            {settings?.hero_intro || "Soul Food City Drinks brengt kleur, energie en tropische smaak naar uw zaak."}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-order-cta"
              onClick={onOrder}
              className="rounded-full bg-gradient-to-r from-sfc-orange to-sfc-red px-7 py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:scale-105"
            >
              Zakelijk bestellen
            </button>
            <button
              data-testid="hero-discover-cta"
              onClick={onDiscover}
              className="rounded-full border border-white/20 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur transition-colors duration-300 hover:bg-white/10"
            >
              Ontdek onze smaken
            </button>
          </motion.div>
        </div>

        <motion.div style={{ y: yFront }} className="relative flex justify-center gap-4 sm:gap-6">
          {FLAVORS.map((f, i) => (
            <motion.div
              key={f.name}
              initial={{ opacity: 0, y: 60, rotate: -4 }}
              animate={{ opacity: 1, y: i === 1 ? -20 : 10, rotate: i === 1 ? 0 : i === 0 ? -5 : 5 }}
              transition={{ duration: 0.9, delay: 0.3 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-1/3 max-w-[160px] overflow-hidden rounded-[2rem] border border-white/10 shadow-2xl"
              style={{ boxShadow: `0 25px 60px -15px ${f.color}66` }}
            >
              <div className="aspect-[3/5] w-full">
                <img src={f.img} alt={f.name} loading="lazy" className="h-full w-full object-cover" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <span className="absolute bottom-3 left-0 right-0 px-2 text-center font-display text-xs font-semibold text-white sm:text-sm" style={{ color: f.color }}>
                {f.name}
              </span>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      <button
        data-testid="scroll-indicator"
        onClick={onDiscover}
        aria-label="Scroll naar beneden"
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-white/60 transition-colors hover:text-white"
      >
        <ChevronDown className="animate-bounce" size={30} />
      </button>
    </section>
  );
}
